import React, { useRef, useState } from "react";
import { UploadCloud, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Uploads an image to object storage and returns the servable URL via onChange.
export default function ImageUpload({ value, onChange, label = "Image", testId = "image-upload", endpoint = "/admin/upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);

  const displaySrc = value ? (value.startsWith("http") ? value : `${BACKEND_URL}${value}`) : "";

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label !== "" && <label className="text-slate-300 text-xs">{label}</label>}
      {displaySrc ? (
        <div className="mt-1 relative group border border-slate-700">
          <img src={displaySrc} alt="" className="w-full h-32 object-cover" data-testid={`${testId}-preview`} />
          <button type="button" onClick={() => onChange("")} className="absolute top-1 right-1 bg-navy/80 p-1 text-slate-200 hover:text-destructive" data-testid={`${testId}-clear`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
          className={`mt-1 cursor-pointer border border-dashed ${drag ? "border-gold bg-gold/5" : "border-slate-700"} h-32 flex flex-col items-center justify-center text-slate-500 hover:border-gold transition-colors`}
          data-testid={testId}
        >
          {uploading ? <Loader2 className="w-6 h-6 animate-spin text-gold" /> : <><UploadCloud className="w-6 h-6 mb-1.5" /><span className="text-xs">Drag &amp; drop or click to upload</span></>}
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-input`} />
    </div>
  );
}
